import { translator } from "../../../../translation/translator";
import commonUtil from "../../../../util/common-utils";

type CategoryReviewRenderValue = string | number | boolean | null | undefined;
type CategoryReviewOptionValue = CategoryReviewRenderValue[] | null | undefined;

const toInt = (value: CategoryReviewRenderValue, fallback = 0): number => commonUtil.toInt(value, fallback);
const REVIEW_DATA_TYPES = ["rating", "text", "single_select", "multi_select"];

type CategoryReviewQuestion = {
    review_question_id?: number;
    data_type?: string;
    question_text?: string;
    question_text_th?: string;
    question_text_en?: string;
    question_text_ja?: string;
    option_th?: string[];
    option_en?: string[];
    option_ja?: string[];
    _draftDataType?: string;
    _draftTextTh?: string;
    _draftTextEn?: string;
    _draftTextJa?: string;
    _draftOptionTh?: string[];
    _draftOptionEn?: string[];
    _draftOptionJa?: string[];
    _isDeleted?: boolean;
};

type CategoryParentRow = {
    category_id?: number;
    category_name?: string;
    products_count?: number;
    _draftName?: string;
    _isEditing?: boolean;
    _isDeleted?: boolean;
    _isNew?: boolean;
    _isLocked?: boolean;
};

type CategoryChildRow = CategoryParentRow & {
    parent_category_id?: number;
    review_config_id?: number;
    _draftReviewConfigId?: number;
};

type ReviewConfigRow = {
    review_config_id?: number;
};

type CategoryReviewRenderState = {
    panelParentEl?: HTMLElement | null;
    panelChildEl?: HTMLElement | null;
    panelReviewEl?: HTMLElement | null;
    reviewSettingsPanelEl?: HTMLElement | null;
    selectedChildParentId: number;
    parents: CategoryParentRow[];
    children: CategoryChildRow[];
    configs: ReviewConfigRow[];
    questionsByConfig: Map<number, CategoryReviewQuestion[]>;
    isParentDirty(parent: CategoryParentRow): boolean;
    isChildDirty(child: CategoryChildRow): boolean;
    isQuestionDirty(question: CategoryReviewQuestion): boolean;
    isReviewConfigDeleted(configId: number): boolean;
    isReviewConfigNew(configId: number): boolean;
    isReviewConfigDirty(configId: number): boolean;
    isReviewConfigEditing(configId: number): boolean;
    canDeleteParentCategory(parent: CategoryParentRow): boolean;
    canDeleteChildCategory(child: CategoryChildRow): boolean;
    getQuestionsByConfig(configId: number): CategoryReviewQuestion[];
    isReviewSettingsOpen?(): boolean;
    canSelectReviewConfig?(configId: number): boolean;
};

type QuestionLabelGetter = {
    getQuestionLabel: (question: CategoryReviewQuestion) => string;
};

export class CategoryReviewEditorRender {
    private readonly getQuestionLabel: (question: CategoryReviewQuestion) => string;

    public constructor({ getQuestionLabel }: QuestionLabelGetter) {
        this.getQuestionLabel = getQuestionLabel;
    }

    public renderAddButton(action: string, labelKey: string): string {
        return `
            <button type="button" class="picker-add picker-add--compact" data-action="${action}">
                <span class="picker-plus" aria-hidden="true">+</span>
                <span class="picker-add-text" data-translate="${labelKey}">เพิ่ม</span>
            </button>
        `;
    }

    public renderRowActions({
        editAction,
        deleteAction,
        extraAction = "",
        extraLabel = "",
        extraAction2 = "",
        extraLabel2 = "",
        idAttr,
        idValue,
        isEditing = false,
        canEdit = true,
        canDelete = false,
        canExtra = true,
        canExtra2 = true,
    }: {
        editAction: string;
        deleteAction: string;
        extraAction?: string;
        extraLabel?: string;
        extraAction2?: string;
        extraLabel2?: string;
        idAttr: string;
        idValue: number;
        isEditing?: boolean;
        canEdit?: boolean;
        canDelete?: boolean;
        canExtra?: boolean;
        canExtra2?: boolean;
    }): string {
        const safeId = commonUtil.escapeHtmlAttribute(String(idValue));
        const activeClass = isEditing ? " is-active" : "";
        const editDisabledAttr = canEdit ? "" : " disabled";
        const deleteDisabledAttr = canDelete ? "" : " disabled";
        const extraDisabledAttr = canExtra ? "" : " disabled";
        const extra2DisabledAttr = canExtra2 ? "" : " disabled";
        const editDisabledClass = canEdit ? "" : " is-disabled";
        const deleteDisabledClass = canDelete ? "" : " is-disabled";
        const extraDisabledClass = canExtra ? "" : " is-disabled";
        const extra2DisabledClass = canExtra2 ? "" : " is-disabled";
        const extraButtonHtml =
            extraAction && extraLabel
                ? `<button type="button" class="category-review-editor__icon-btn category-review-editor__icon-btn--addq${extraDisabledClass}" data-action="${extraAction}" ${idAttr}="${safeId}" title="Add Question" aria-label="Add Question"${extraDisabledAttr}>${commonUtil.escapeHtml(extraLabel)}</button>`
                : "";
        const extraButton2Html =
            extraAction2 && extraLabel2
                ? `<button type="button" class="category-review-editor__icon-btn category-review-editor__icon-btn--delq${extra2DisabledClass}" data-action="${extraAction2}" ${idAttr}="${safeId}" title="Remove Question" aria-label="Remove Question"${extra2DisabledAttr}>${commonUtil.escapeHtml(extraLabel2)}</button>`
                : "";
        return `
            <div class="category-review-editor__row-actions">
                <button type="button" class="btn-circle edit category-review-editor__icon-btn category-review-editor__icon-btn--edit${activeClass}${editDisabledClass}" data-action="${editAction}" ${idAttr}="${safeId}" title="Edit" aria-label="Edit"${editDisabledAttr}></button>
                <button type="button" class="btn-circle delete category-review-editor__icon-btn category-review-editor__icon-btn--delete${deleteDisabledClass}" data-action="${deleteAction}" ${idAttr}="${safeId}" title="Delete" aria-label="Delete"${deleteDisabledAttr}></button>
                ${extraButtonHtml}
                ${extraButton2Html}
            </div>
        `;
    }

    public getParentStatus(
        parent: CategoryParentRow,
        state: CategoryReviewRenderState,
    ): { label: string; rowClass: string } {
        if (parent?._isDeleted) return { label: "Deleted", rowClass: "is-deleted" };
        if (parent?._isNew) return { label: "Added", rowClass: "is-added" };
        if (state.isParentDirty(parent)) return { label: "Edited", rowClass: "is-edited" };
        return { label: "", rowClass: "" };
    }

    public getChildStatus(
        child: CategoryChildRow,
        state: CategoryReviewRenderState,
    ): { label: string; rowClass: string } {
        if (child?._isDeleted) return { label: "Deleted", rowClass: "is-deleted" };
        if (child?._isNew) return { label: "Added", rowClass: "is-added" };
        if (state.isChildDirty(child)) return { label: "Edited", rowClass: "is-edited" };
        return { label: "", rowClass: "" };
    }

    public getQuestionTextByLang(question: CategoryReviewQuestion, lang: string): string {
        const keyByLang: Record<string, string> = {
            th: "question_text_th",
            en: "question_text_en",
            ja: "question_text_ja",
        };
        const key = keyByLang[lang] || "question_text";
        return String(question?.[key] ?? question?.question_text ?? "").trim();
    }

    public getQuestionDraftByLang(question: CategoryReviewQuestion, lang: string): string {
        const draftKeyByLang: Record<string, string> = {
            th: "_draftTextTh",
            en: "_draftTextEn",
            ja: "_draftTextJa",
        };
        const draftKey = draftKeyByLang[lang] || "_draftTextJa";
        const fallback = this.getQuestionTextByLang(question, lang);
        return String(question?.[draftKey] ?? fallback ?? "");
    }

    public isQuestionLangDirty(
        question: CategoryReviewQuestion,
        state: CategoryReviewRenderState,
        lang: string,
    ): boolean {
        if (!question) return false;
        const original = this.getQuestionTextByLang(question, lang);
        const draft = this.getQuestionDraftByLang(question, lang);
        return String(draft).trim() !== String(original).trim() || state.isQuestionDirty(question);
    }

    public getQuestionOptionsByLang(question: CategoryReviewQuestion, lang: string): string[] {
        const optionKeyByLang: Record<string, string> = {
            th: "option_th",
            en: "option_en",
            ja: "option_ja",
        };
        const draftKeyByLang: Record<string, string> = {
            th: "_draftOptionTh",
            en: "_draftOptionEn",
            ja: "_draftOptionJa",
        };
        const optionKey = optionKeyByLang[lang] || "option_ja";
        const draftKey = draftKeyByLang[lang] || "_draftOptionJa";
        const draft = question?.[draftKey];
        if (Array.isArray(draft)) return draft;
        const original = question?.[optionKey];
        return Array.isArray(original) ? original : [];
    }

    public formatOptions(options: CategoryReviewOptionValue): string {
        if (!Array.isArray(options)) return "-";
        const normalized = options.map((item) => String(item || "").trim()).filter((item) => item !== "");
        return normalized.length > 0 ? normalized.join(", ") : "-";
    }

    public getReviewConfigStatus(
        configId: number,
        state: CategoryReviewRenderState,
    ): { label: string; rowClass: string } {
        if (state.isReviewConfigDeleted(configId)) return { label: "Deleted", rowClass: "is-deleted" };
        if (state.isReviewConfigNew(configId)) return { label: "Added", rowClass: "is-added" };
        if (state.isReviewConfigDirty(configId)) return { label: "Edited", rowClass: "is-edited" };
        return { label: "", rowClass: "" };
    }

    public renderParentTable(state: CategoryReviewRenderState): void {
        if (!(state.panelParentEl instanceof HTMLElement)) return;

        const rowsHtml = [...state.parents]
            .sort((a, b) => toInt(a.category_id, 0) - toInt(b.category_id, 0))
            .map((parent) => {
                const parentId = toInt(parent.category_id, 0);
                const name = String(parent._draftName ?? parent.category_name ?? "");
                const isEditing = parent._isEditing === true;
                const isDirty = state.isParentDirty(parent);
                const isDeleted = parent._isDeleted === true;
                const canEdit = parent._isLocked !== true && !isDeleted;
                const canDelete = isDeleted || state.canDeleteParentCategory(parent);
                const status = this.getParentStatus(parent, state);
                const nameToneClass = isDeleted
                    ? " text-danger text-decoration-line-through"
                    : isDirty
                      ? " text-danger"
                      : " text-dark";
                const nameCell = isEditing
                    ? `<input type="text" class="form-control form-control-sm${isDirty ? " text-danger" : " text-dark"}" data-field="parent_category_name" data-parent-id="${parentId}"
                                value="${commonUtil.escapeHtmlAttribute(name)}">`
                    : `<div class="category-review-editor__readonly${nameToneClass}">${commonUtil.escapeHtml(name || "-")}</div>`;
                return `
                    <div data-parent-id="${parentId}" class="category-review-editor__table-row category-review-editor__row ${status.rowClass}">
                        <div class="category-review-editor__cell category-review-editor__readonly category-review-editor__num-cell">${parentId}</div>
                        <div class="category-review-editor__cell">
                            ${nameCell}
                        </div>
                        <div class="category-review-editor__cell category-review-editor__readonly category-review-editor__num-cell">${Math.max(0, toInt(parent.products_count, 0))}</div>
                        <div class="category-review-editor__cell category-review-editor__actions-cell">
                            ${this.renderRowActions({
                                editAction: "edit-parent",
                                deleteAction: "delete-parent",
                                idAttr: "data-parent-id",
                                idValue: parentId,
                                isEditing,
                                canEdit,
                                canDelete,
                            })}
                        </div>
                        <div class="category-review-editor__cell category-review-editor__readonly category-review-editor__status-cell">${status.label || "-"}</div>
                    </div>
                `;
            })
            .join("");

        state.panelParentEl.innerHTML = `
            <div class="category-review-editor__table-wrap">
                <div class="category-review-editor__table category-review-editor__table--parent" role="table" aria-label="Parent Categories">
                    <div class="category-review-editor__table-header" role="row">
                        <div class="category-review-editor__cell category-review-editor__num-cell" role="columnheader" data-translate="category_review_editor_parent_category_id">parent_category_id</div>
                        <div class="category-review-editor__cell" role="columnheader" data-translate="category_review_editor_parent_category_name">parent_category_name</div>
                        <div class="category-review-editor__cell category-review-editor__num-cell" role="columnheader" data-translate="category_review_editor_item_count">item_count</div>
                        <div class="category-review-editor__cell category-review-editor__actions-cell" role="columnheader" data-translate="category_review_editor_actions">actions</div>
                        <div class="category-review-editor__cell category-review-editor__status-cell" role="columnheader" data-translate="category_review_editor_status">status</div>
                    </div>
                    <div class="category-review-editor__table-body" role="rowgroup">
                        ${rowsHtml || '<div class="category-review-editor__table-row"><div class="category-review-editor__cell category-review-editor__cell--empty" data-translate="category_review_editor_no_data">ไม่มีข้อมูล</div></div>'}
                    </div>
                </div>
            </div>
        `;

        translator.updateUITranslations();
    }

    public renderChildTable(state: CategoryReviewRenderState): void {
        if (!(state.panelChildEl instanceof HTMLElement)) return;

        const selectedParentId = toInt(state.selectedChildParentId, 0);
        const parentOptionsHtml = [...state.parents]
            .filter((parent) => !parent._isDeleted)
            .sort((a, b) => toInt(a.category_id, 0) - toInt(b.category_id, 0))
            .map((parent) => {
                const parentId = toInt(parent.category_id, 0);
                const parentName = String(parent._draftName ?? parent.category_name ?? "").trim() || "-";
                const selected = selectedParentId === parentId ? " selected" : "";
                return `<option value="${commonUtil.escapeHtmlAttribute(String(parentId))}"${selected}>${commonUtil.escapeHtml(`${parentId} - ${parentName}`)}</option>`;
            })
            .join("");

        const rowsHtml = [...state.children]
            .filter((child) => toInt(child.parent_category_id, 0) === selectedParentId)
            .sort((a, b) => toInt(a.category_id, 0) - toInt(b.category_id, 0))
            .map((child) => {
                const childId = toInt(child.category_id, 0);
                const childName = String(child._draftName ?? child.category_name ?? "");
                const isEditing = child._isEditing === true;
                const isDirty = state.isChildDirty(child);
                const isDeleted = child._isDeleted === true;
                const canEdit = child._isLocked !== true && !isDeleted;
                const canDelete = isDeleted || state.canDeleteChildCategory(child);
                const status = this.getChildStatus(child, state);
                const reviewConfigId = toInt(child.review_config_id, 0);
                const draftReviewConfigId = toInt(child._draftReviewConfigId ?? reviewConfigId, 0);
                const nameToneClass = isDeleted
                    ? " text-danger text-decoration-line-through"
                    : isDirty
                      ? " text-danger"
                      : " text-dark";
                const childNameCell = isEditing
                    ? `<input type="text" class="form-control form-control-sm${isDirty ? " text-danger" : " text-dark"}" data-field="child_category_name" data-child-id="${childId}"
                                value="${commonUtil.escapeHtmlAttribute(childName)}">`
                    : `<div class="category-review-editor__readonly${nameToneClass}">${commonUtil.escapeHtml(childName || "-")}</div>`;
                const displayConfigId = draftReviewConfigId;
                const reviewConfigCell =
                    displayConfigId > 0
                        ? `<button type="button" class="category-review-editor__review-config-link" data-action="open-review-settings" data-config-id="${displayConfigId}" title="Change Review Questions" aria-label="Change Review Questions">${displayConfigId}</button>`
                        : '<button type="button" class="category-review-editor__review-config-link" data-action="open-review-settings" data-config-id="0" title="Change Review Questions" aria-label="Change Review Questions">+</button>';
                return `
                    <div data-child-id="${childId}" class="category-review-editor__table-row category-review-editor__row ${status.rowClass}">
                        <div class="category-review-editor__cell category-review-editor__readonly category-review-editor__num-cell">${childId > 0 ? childId : "-"}</div>
                        <div class="category-review-editor__cell">
                            ${childNameCell}
                        </div>
                        <div class="category-review-editor__cell category-review-editor__readonly category-review-editor__num-cell">${Math.max(0, toInt(child.products_count, 0))}</div>
                        <div class="category-review-editor__cell category-review-editor__actions-cell">
                            ${reviewConfigCell}
                        </div>
                        <div class="category-review-editor__cell category-review-editor__actions-cell">
                            ${this.renderRowActions({
                                editAction: "edit-child",
                                deleteAction: "delete-child",
                                idAttr: "data-child-id",
                                idValue: childId,
                                isEditing,
                                canEdit,
                                canDelete,
                            })}
                        </div>
                        <div class="category-review-editor__cell category-review-editor__readonly category-review-editor__status-cell">${status.label || "-"}</div>
                    </div>
                `;
            })
            .join("");

        state.panelChildEl.innerHTML = `
            <div class="category-review-editor__child-toolbar">
                <label class="category-review-editor__child-toolbar-label" for="category-review-editor-child-parent-filter" data-translate="category_review_editor_parent_category_filter">Parent Category</label>
                <select id="category-review-editor-child-parent-filter" class="form-control form-control-sm category-review-editor__child-parent-select" data-action="child-parent-filter">
                    ${parentOptionsHtml}
                </select>
            </div>
            <div class="category-review-editor__table-wrap">
                <div class="category-review-editor__table category-review-editor__table--child" role="table" aria-label="Child Categories">
                    <div class="category-review-editor__table-header" role="row">
                        <div class="category-review-editor__cell category-review-editor__num-cell" role="columnheader" data-translate="category_review_editor_child_category_id">child_category_id</div>
                        <div class="category-review-editor__cell" role="columnheader" data-translate="category_review_editor_child_category_name">child_category_name</div>
                        <div class="category-review-editor__cell category-review-editor__num-cell" role="columnheader" data-translate="category_review_editor_item_count">item_count</div>
                        <div class="category-review-editor__cell category-review-editor__actions-cell" role="columnheader"data-translate="category_review_editor_review_config_id">review_config_id</div>
                        <div class="category-review-editor__cell category-review-editor__actions-cell" role="columnheader" data-translate="category_review_editor_actions">actions</div>
                        <div class="category-review-editor__cell category-review-editor__status-cell" role="columnheader" data-translate="category_review_editor_status">status</div>
                    </div>
                    <div class="category-review-editor__table-body" role="rowgroup">
                        ${rowsHtml || '<div class="category-review-editor__table-row"><div class="category-review-editor__cell category-review-editor__cell--empty" data-translate="category_review_editor_no_data">ไม่มีข้อมูล</div></div>'}
                    </div>
                </div>
            </div>
        `;

        translator.updateUITranslations();
    }

    public renderReviewTable(state: CategoryReviewRenderState, targetEl: HTMLElement | null = null): void {
        if (!(targetEl instanceof HTMLElement)) {
            if (state.panelReviewEl instanceof HTMLElement) {
                this.renderReviewTable(state, state.panelReviewEl);
            }
            if (
                typeof state.isReviewSettingsOpen === "function" &&
                state.isReviewSettingsOpen() &&
                state.reviewSettingsPanelEl instanceof HTMLElement
            ) {
                this.renderReviewTable(state, state.reviewSettingsPanelEl);
            }
            return;
        }
        const panelEl = targetEl;

        const activeChildren = state.children.filter((c) => !c._isDeleted);
        const configIds = Array.from(
            new Set([
                ...state.questionsByConfig.keys(),
                ...state.configs.map((c) => toInt(c.review_config_id, 0)),
                ...activeChildren.map((c) => toInt(c.review_config_id, 0)),
                ...activeChildren.map((c) => toInt(c._draftReviewConfigId ?? 0, 0)),
            ]),
        )
            .filter((id) => id > 0)
            .sort((a, b) => a - b);

        const isModalTarget = panelEl === state.reviewSettingsPanelEl;

        const maxQuestionCount = Math.max(
            1,
            ...configIds.map((configId) => {
                const questions = (state.questionsByConfig.get(configId) || []).filter((q) => !q._isDeleted);
                return questions.length;
            }),
        );

        const rowsHtml = configIds
            .map((configId) => {
                const questions = state.getQuestionsByConfig(configId);
                const activeQuestions = questions.filter((q) => !q._isDeleted);
                const rowStatus = this.getReviewConfigStatus(configId, state);
                const isEditing = state.isReviewConfigEditing(configId);
                const canEdit = activeQuestions.length > 0;
                const canDelete = questions.length > 0;
                const canAddQuestion = !state.isReviewConfigDeleted(configId);
                const canRemoveQuestion = !state.isReviewConfigDeleted(configId) && activeQuestions.length > 0;
                const canSelect =
                    isModalTarget &&
                    typeof state.canSelectReviewConfig === "function" &&
                    state.canSelectReviewConfig(configId);

                const qCells = Array.from({ length: maxQuestionCount }, (_, i) => {
                    const q = activeQuestions[i] || null;
                    if (!q) {
                        return `
                            <div class="category-review-editor__cell category-review-editor__cell--question-empty">
                                <div class="category-review-editor__question-cell category-review-editor__question-cell--empty" aria-hidden="true">
                                </div>
                            </div>
                        `;
                    }

                    const questionId = toInt(q.review_question_id, 0);
                    const dataTypeValue = String(q._draftDataType ?? q.data_type ?? "text");
                    const dataTypeOptions = REVIEW_DATA_TYPES.map((type) => {
                        const selected = dataTypeValue === type ? " selected" : "";
                        return `<option value="${type}"${selected}>${type}</option>`;
                    }).join("");
                    const isSelectType = dataTypeValue === "single_select" || dataTypeValue === "multi_select";
                    const dataTypeLine = isEditing
                        ? `<div class="category-review-editor__question-line"><span class="category-review-editor__question-lang">TYPE</span><select class="form-control form-control-sm" data-field="question_data_type" data-question-id="${questionId}" data-config-id="${configId}">${dataTypeOptions}</select></div>`
                        : `<div class="category-review-editor__question-line"><span class="category-review-editor__question-lang">TYPE</span><span class="category-review-editor__readonly">${commonUtil.escapeHtml(dataTypeValue || "-")}</span></div>`;
                    const langs = ["th", "en", "ja"];
                    const questionLines = langs
                        .map((langKey) => {
                            const upper = langKey.toUpperCase();
                            const field = `question_text_${langKey}`;
                            const value = this.getQuestionDraftByLang(q, langKey);
                            const isDirty = this.isQuestionLangDirty(q, state, langKey);
                            const control = isEditing
                                ? `<input type="text" class="form-control form-control-sm${isDirty ? " text-danger" : ""}" data-field="${field}" data-question-id="${questionId}" data-config-id="${configId}" data-validated="required" required value="${commonUtil.escapeHtmlAttribute(value)}">`
                                : `<span class="category-review-editor__readonly${isDirty ? " text-danger" : ""}">${commonUtil.escapeHtml(value || "-")}</span>`;

                            return `<div class="category-review-editor__question-line"><span class="category-review-editor__question-lang">${upper}</span>${control}</div>`;
                        })
                        .join("");

                    const optionContent = isSelectType
                        ? `
                            <div class="category-review-editor__question-options-action"><button type="button" class="btn btn-outline-secondary btn-sm" data-action="edit-question-options" data-config-id="${configId}" data-question-id="${questionId}">Options</button></div>
                        `
                        : "";

                    return `
                        <div class="category-review-editor__cell">
                            <div class="category-review-editor__question-cell${isEditing ? "" : " category-review-editor__question-cell--readonly"}">
                                ${dataTypeLine}
                                ${questionLines}
                                ${optionContent}
                            </div>
                        </div>
                    `;
                }).join("");

                return `
                    <div data-config-id="${configId}" class="category-review-editor__table-row category-review-editor__row ${rowStatus.rowClass}">
                        <div class="category-review-editor__cell category-review-editor__readonly">${configId}</div>
                        ${qCells}
                        <div class="category-review-editor__cell category-review-editor__actions-cell">
                            ${this.renderRowActions({
                                editAction: "edit-review",
                                deleteAction: "delete-review",
                                extraAction: "add-review-question",
                                extraLabel: "+Q",
                                extraAction2: "remove-review-question",
                                extraLabel2: "-Q",
                                idAttr: "data-config-id",
                                idValue: configId,
                                isEditing,
                                canEdit,
                                canDelete,
                                canExtra: canAddQuestion,
                                canExtra2: canRemoveQuestion,
                            })}
                        </div>
                        <div class="category-review-editor__cell category-review-editor__readonly category-review-editor__status-cell">${rowStatus.label || "-"}</div>
                        ${
                            isModalTarget
                                ? `<div class="category-review-editor__cell category-review-editor__actions-cell">
                                        <button type="button" class="btn btn-outline-secondary btn-sm" data-action="select-review-config" data-config-id="${configId}" ${canSelect ? "" : "disabled"}>Select</button>
                                    </div>`
                                : ""
                        }
                    </div>
                `;
            })
            .join("");

        const reviewHeaderCells = [
            '<div class="category-review-editor__cell" role="columnheader">Config Id</div>',
            ...Array.from(
                { length: maxQuestionCount },
                (_, i) =>
                    `<div class="category-review-editor__cell category-review-editor__question-header" role="columnheader"><div class="category-review-editor__question-header-title">Q${i + 1}</div></div>`,
            ),
            '<div class="category-review-editor__cell category-review-editor__actions-cell" role="columnheader" data-translate="category_review_editor_actions">actions</div>',
            '<div class="category-review-editor__cell category-review-editor__status-cell" role="columnheader" data-translate="category_review_editor_status">status</div>',
            isModalTarget
                ? '<div class="category-review-editor__cell category-review-editor__actions-cell" role="columnheader">Select</div>'
                : "",
        ].join("");

        const reviewTableClass = isModalTarget
            ? "category-review-editor__table category-review-editor__table--review is-modal-target"
            : "category-review-editor__table category-review-editor__table--review";

        panelEl.innerHTML = `
            <div class="category-review-editor__table-wrap">
                <div class="${reviewTableClass}" style="--q-count:${maxQuestionCount};" role="table" aria-label="Review Questions">
                    <div class="category-review-editor__table-header" role="row">
                        ${reviewHeaderCells}
                    </div>
                    <div class="category-review-editor__table-body" role="rowgroup">
                        ${rowsHtml || '<div class="category-review-editor__table-row"><div class="category-review-editor__cell category-review-editor__cell--empty" data-translate="category_review_editor_no_data">ไม่มีข้อมูล</div></div>'}
                    </div>
                </div>
            </div>
        `;

        translator.updateUITranslations();
    }
}
